import React, { useState, useEffect, useRef } from 'react';
import styled from 'astroturf/react';

const Container = styled.div`
  position: relative;
`;

const PresetHeightContainer = styled.div`
  position: absolute;
  visibility: hidden;
  height: auto;
  width: 100%;
`;

type TypeWriterProps = {
  /** The text content to be displayed with the typewriter effect */
  text: string;
  /** The speed of the typewriter effect in milliseconds per word. Defaults to 30ms */
  speed?: number;
  /** Whether to preset the container height to match the full text height */
  presetHeight?: boolean;
  /** Optional callback function that fires when the typewriter effect completes */
  onComplete?: () => void;
};

export const TypeWriter: React.FC<TypeWriterProps> = ({
  text,
  speed = 50,
  presetHeight = false,
  onComplete = () => null,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hiddenTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!text) return;

    const words = text.split(' ');

    if (currentIndex >= words.length) {
      onComplete();
      return;
    }

    // Add extra delay for words with punctuation
    const currentWord = words[currentIndex];
    const hasPunctuation = /[.,!?;:]/.test(currentWord);
    const delay = hasPunctuation ? speed * 6 : speed;

    const timer = setTimeout(() => {
      setDisplayedText(
        (prev) => prev + (currentIndex === 0 ? '' : ' ') + words[currentIndex],
      );
      setCurrentIndex((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [currentIndex, text, speed, onComplete]);

  // Reset state when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);

  return (
    <Container>
      <div
        style={{
          height: presetHeight ? hiddenTextRef.current?.offsetHeight : 'auto',
        }}
      >
        {displayedText}
      </div>
      {presetHeight && (
        <PresetHeightContainer ref={hiddenTextRef}>
          {text}
        </PresetHeightContainer>
      )}
    </Container>
  );
};
